/*
 * Copyright (c) 2023 SmartWifi - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential. No warranty, explicit or implicit provided. In
 * no event will the author be liable for any claim or damages.
 */

import { useContext } from "react";
import { AccountContext, ContextUpdater } from "../context/ContextProvider";
import { Label } from "reactstrap";
import {
  requestBrandingGet,
  requestCaptivePortalLayout,
  requestClient,
  requestClientInfoAndTemplate,
  requestMarketingHomeInfo,
  requestRouterConfiguration
} from "../context/RequestManager";
import ItemLabel from "../util/Icons";
import debug from "../util/constants/Debug";

const ItemLink = ({
  type,
  item,
  showIcon = false,
  disabled = false,
  className,
  tag,
  onClick,
  skipAction = false,
  preventPropagation = false,
  children,
}) => {
  // contexts
  const updateContexts = useContext(ContextUpdater);
  const accountContext = useContext(AccountContext);
  const isAdmin = (accountContext.admin ?? false) || (accountContext.subAdmin ?? false);

  // handlers
  const handleClientSelected = (client) => {
    const { id, name } = client;
    accountContext.updateState({
      clientId: id,
      clientSummary: { id, name }
    });
  };

  const handleBrandSelected = (brand) => {
    const { id } = brand;
    accountContext.updateState({ 
      brandId: id
    })
  }

  const handleClick = (event) => {
    if (disabled) {
      return;
    }

    // validate state
    const itemId = item?.id;
    if (itemId == null) {
      debug.warn("Invalid state");
      return;
    }

    // prepare common fields
    const selectClient = () => handleClientSelected({ id: item?.clientId, name: item?.clientName });
    const commonRequestFields = {
      context: updateContexts,
      token: accountContext.token
    };

    // call on click handler if provided
    onClick?.(item);

    if (preventPropagation) {
      event.stopPropagation();
    }

    // skip item-specific action if needed
    if (skipAction) {
      return;
    }

    // make item-specific request
    switch (type) {
      case "client":
        // select item and to go business info page
        handleClientSelected(item);
        requestClientInfoAndTemplate({
          ...commonRequestFields,
          clientId: itemId,
          navigate: (_) => "/dashboard"
        });
        break;
      case "location":
        selectClient();
        requestClientInfoAndTemplate({
          ...commonRequestFields,
          clientId: item.clientId,
          navigate: (_) => "/dashboard",
        });
        break;
      case "router":
        if (isAdmin) {
          // view router status if admin
          requestRouterConfiguration({
            context: updateContexts,
            token: accountContext.token,
            clientId: item.clientId,
            macAddress: item.macAddress,
            // includeAllRouters: true,
            navigate: (_) => "/router-status",
          });
        } else {
          // go to client info page if not admin
          requestClient({
            ...commonRequestFields,
            clientId: item.clientId,
            settingsPage: "",
            navigate: (_) => "/business-settings",
          });
        }
        break;
      case "account": 
        selectClient();
        requestClient({
          ...commonRequestFields,
          clientId: item.clientId,
          settingsPage: "",
          navigate: (_) => "/business-settings",
        });
        break;
      case "template":
        selectClient();
        requestClient({
          ...commonRequestFields,
          clientId: item.clientId,
          settingsPage: "template",
          navigate: (_) => "/business-settings",
        });
        requestClientInfoAndTemplate({
          ...commonRequestFields,
          clientId: item.clientId,
          settingsPage: "template",
          navigate: (_) => "/business-settings",
        });
        break;
      case "captive-portal":
        requestCaptivePortalLayout({
          context: updateContexts,
          token: accountContext.token,
          layoutId: itemId?.toString(),
          clientId: item.clientId,
          contextKey: "createLoginPage",
          layoutKey: "initialLayout",
          showLoading: true,
          navigate: (_) => "/login-pages"
        });
        break;
      case "marketing":
        // select client and navigate to marketing page
        selectClient();
        requestMarketingHomeInfo({
          ...commonRequestFields,
          clientId: item.clientId,
          navigate: (_) => "/marketing"
        });
        break;
      case "brand":
        handleBrandSelected(item);
        requestBrandingGet({
          context: updateContexts,
          token: accountContext.token,
          id: itemId,
          domainBrand: itemId != null && itemId === accountContext.domainBrand?.id,
          navigate: (_) => "/brand-settings"
        });
      default: break;
    }
  };

  return (
    <Label
      className={`${className} clickable no-select ${disabled ? "text-muted" : "link-primary"}`}
      tag={tag}
      onClick={(event) => handleClick(event)}
    >
      {showIcon ? <ItemLabel type={type}>{children}</ItemLabel> : children}
    </Label>
  );
};

export default ItemLink;
